// services/CareTeamService.js — the provider portal and the player's care team.
import axios from "axios";
import { getAuthHeaders } from "./GameService";

const SERVER_URL = process.env.REACT_APP_SERVER_URL;
const api = (path) => `${SERVER_URL}/api/${path}`;
const auth = () => ({ headers: getAuthHeaders() });

// --- Provider accounts ---

export const providerSignIn = async (email, password) => {
  const response = await axios.post(api("provider/signin/"), { email, password });
  return response.data; // { access, refresh, user }
};

export const providerSignUp = async (data) => {
  const response = await axios.post(api("provider/signup/"), data);
  return response.data; // { access, refresh, user }
};

// Emails a reset link (the same Djoser reset players use). It succeeds whether
// or not an account has that address.
export const requestPasswordReset = async (email) => {
  await axios.post(`${SERVER_URL}/auth/users/reset_password/`, { email });
};

export const getProviderMe = async () => {
  const response = await axios.get(api("provider/me/"), auth());
  return response.data;
};

// --- Roster ---

export const getRoster = async () => {
  const response = await axios.get(api("provider/roster/"), auth());
  return response.data.patients;
};

// One invite works for new and existing players. With an email it is sent
// straight away; emailed_at stays null if sending failed.
export const createInvite = async ({ patient_name, email, year_of_birth, gentle_start }) => {
  const response = await axios.post(
    api("provider/invites/"),
    { patient_name, email, year_of_birth, gentle_start },
    auth(),
  );
  return response.data;
};

export const emailInvite = async (inviteId, email) => {
  const response = await axios.post(
    api(`provider/invites/${inviteId}/email/`),
    { email },
    auth(),
  );
  return response.data;
};

export const cancelInvite = async (inviteId) => {
  await axios.post(api(`provider/invites/${inviteId}/cancel/`), {}, auth());
};

export const cancelRequest = async (requestId) => {
  await axios.post(api(`provider/requests/${requestId}/cancel/`), {}, auth());
};

// --- Patient detail ---

export const getPatientDetail = async (patientId) => {
  const response = await axios.get(api(`provider/patients/${patientId}/`), auth());
  return response.data;
};

export const getPatientSessionsPage = async (patientId, offset = 0, limit = 8) => {
  const response = await axios.get(api(`provider/patients/${patientId}/sessions/`), {
    ...auth(),
    params: { offset, limit },
  });
  return response.data; // { total, results }
};

export const downloadPatientExport = async (patientId) => {
  const response = await axios.get(api(`provider/patients/${patientId}/export/`), {
    ...auth(),
    responseType: "blob",
  });
  const disposition = response.headers?.["content-disposition"] || "";
  const match = disposition.match(/filename="([^"]+)"/);
  const filename = match ? match[1] : "sessions.csv";
  const url = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
  return filename;
};

export const getPatientNotes = async (patientId) => {
  const response = await axios.get(
    api(`provider/patients/${patientId}/notes/`),
    auth(),
  );
  return response.data;
};

export const addPatientNote = async (patientId, text) => {
  const response = await axios.post(
    api(`provider/patients/${patientId}/notes/`),
    { text },
    auth(),
  );
  return response.data;
};

export const removePatient = async (patientId) => {
  await axios.post(api(`provider/patients/${patientId}/remove/`), {}, auth());
};

export const getGameSettings = async (patientId, gameId) => {
  const response = await axios.get(
    api(`patients/${patientId}/game-settings/${gameId}/`),
    auth(),
  );
  return response.data.settings || {};
};

export const patchGameSettings = async (patientId, gameId, changes) => {
  const response = await axios.patch(
    api(`patients/${patientId}/game-settings/${gameId}/`),
    { settings: changes },
    auth(),
  );
  return response.data.settings;
};

// --- The /join/CODE page ---

// Resolves to { code, provider, already_connected, expires_at, patient };
// rejects with error.response.data.error set to not_found | expired.
export const getJoinInvite = async (code) => {
  const response = await axios.get(api(`join/${encodeURIComponent(code)}/`), auth());
  return response.data;
};

// Create an account that shares with the provider behind the code at once.
// Resolves to { access, refresh, user }.
export const registerWithCode = async (data) => {
  const response = await axios.post(api("register/"), data);
  return response.data;
};

// --- Player: My care team ---

export const getCareTeam = async () => {
  const response = await axios.get(api("care-team/"), auth());
  return response.data; // { connected, pending }
};

// Resolves to { code, provider }; rejects with error.response.data.error set
// to format | not_found | expired | already_connected.
export const checkProviderCode = async (code) => {
  const response = await axios.post(api("care-team/check-code/"), { code }, auth());
  return response.data;
};

export const approveProviderCode = async (code) => {
  const response = await axios.post(api("care-team/approve-code/"), { code }, auth());
  return response.data;
};

export const approveRequest = async (linkId) => {
  const response = await axios.post(
    api(`care-team/requests/${linkId}/approve/`),
    {},
    auth(),
  );
  return response.data;
};

export const declineRequest = async (linkId) => {
  await axios.post(api(`care-team/requests/${linkId}/decline/`), {}, auth());
};

export const stopSharing = async (linkId) => {
  await axios.post(api(`care-team/connections/${linkId}/stop/`), {}, auth());
};
